/** Shared chrome. Theme preferences and Agent scope are supplied by its owner. */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { DropdownMenu as Dropdown } from "radix-ui";
import { IconButton, Tooltip } from "../components/rumonai";
import { LightMotion } from "../components/LightMotion";
import type { HubAgent } from "../lib/api";
import type { Theme } from "../lib/appearance";
import { Icon, type IconName } from "../components/Icon";
import { Avatar } from "./Avatar";
import { instanceStatus } from "../hub/api";
import type { WorkTools } from "./workTools";
import "./StudioChrome.css";

export function ThemeSwitch({
  theme,
  onPick,
  settingsHref = "/#/settings?section=workspace",
}: {
  theme: Theme;
  onPick: (next: Theme) => void;
  settingsHref?: string;
}) {
  return <PopMenu label="切换主题" trigger={<Icon name="theme" size={20} />}>
    <MenuLabel>主题</MenuLabel>
    <Dropdown.RadioGroup value={theme} onValueChange={value => onPick(value as Theme)}>
      {(["system", "light", "dark"] as Theme[]).map(value => <Dropdown.RadioItem key={value} value={value} className="pa-menu-item">
        {value === "system" ? "跟随系统" : value === "light" ? "亮" : "暗"}
        <Dropdown.ItemIndicator><Icon name="yes" size={18} /></Dropdown.ItemIndicator>
      </Dropdown.RadioItem>)}
    </Dropdown.RadioGroup>
    <MenuItem href={settingsHref}>配色与背景<Icon name="arrow" size={14} /></MenuItem>
  </PopMenu>;
}

/** The shared menu surface keeps real route links and Radix focus handling. */
export function PopMenu({ label, trigger, align = "end", className = "", triggerClass = "pa-icon-button", children }: {
  label: string;
  trigger: ReactNode;
  align?: "start" | "end";
  className?: string;
  triggerClass?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const focusMenu = useCallback((panel: HTMLDivElement | null) => {
    const selected = panel?.querySelector<HTMLElement>('[role="menuitem"][aria-current]:not(:disabled), [role="menuitemradio"][aria-checked="true"]')
      ?? panel?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled), [role="menuitemradio"]');
    selected?.focus();
  }, []);
  useEffect(() => {
    const close = () => setOpen(false);
    window.addEventListener("hashchange", close);
    return () => window.removeEventListener("hashchange", close);
  }, []);
  return <span className={`pop-menu ${className}`.trim()}><Dropdown.Root open={open} onOpenChange={setOpen} modal={false}>
    <Dropdown.Trigger asChild><button type="button" className={triggerClass} aria-label={label}>{trigger}</button></Dropdown.Trigger>
    <Dropdown.Portal><Dropdown.Content ref={focusMenu} className="pa-popup pa-menu-content" aria-label={label} align={align} sideOffset={8} loop>{children}</Dropdown.Content></Dropdown.Portal>
  </Dropdown.Root></span>;
}

export function MenuItem({ icon, children, href, onSelect, disabled = false, note, current = false }: {
  icon?: IconName;
  children: ReactNode;
  href?: string;
  onSelect?: () => void;
  disabled?: boolean;
  note?: ReactNode;
  current?: boolean;
}) {
  const body = <>{icon ? <Icon name={icon} size={20} /> : null}<span className="menu-item-label">{children}</span>{note ? <span className="menu-item-note">{note}</span> : null}</>;
  return <Dropdown.Item asChild disabled={disabled} onSelect={onSelect}>
    {href && !disabled
      ? <a className="pa-menu-item" href={href} aria-current={current ? "true" : undefined}>{body}</a>
      : <button type="button" className="pa-menu-item" disabled={disabled} aria-current={current ? "true" : undefined}>{body}</button>}
  </Dropdown.Item>;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <Dropdown.Label className="pa-menu-label">{children}</Dropdown.Label>;
}

/* ---------- the Agent's bar ---------- */

/** The header and its switch menu use the same portrait as the navigation. */
export function AgentAvatar({ name, identity, size = "md" }: { name: string; identity?: string | null; size?: "md" | "sm" }) {
  return <Avatar name={name} identity={identity} size={size} className="agent-avatar" />;
}

/**
 * Which Agent this is, and the way to the others. With nobody else to go to the name is just a name;
 * with others it is a button that lists them. Only the route changes: each Agent keeps its own document.
 */
export function AgentSwitch({
  agents,
  current,
  name: given,
  destinations = {},
}: {
  agents: HubAgent[];
  current: string | null;
  /** The name to show when the directory has not said one. */
  name?: string;
  destinations?: Record<string, string>;
}) {
  const here = agents.find(agent => agent.id === current);
  const name = here?.name || given || "创作助手";
  const others = agents.filter(agent => agent.id !== current);
  const face = <>
    <AgentAvatar name={name} identity={current} />
    <span className="agent-switch-name" data-quoted="true">{name}</span>
  </>;
  if (!others.length)
    return <div className="agent-switch" data-testid="agent-switch"><span className="agent-switch-static">{face}</span></div>;
  return (
    <div className="agent-switch" data-testid="agent-switch">
      <PopMenu label="切换 IP" align="start" triggerClass="btn pa-btn ghost agent-switch-trigger"
        trigger={<>{face}<Icon name="down" size={14} className="icon agent-switch-caret" /></>}>
        <MenuLabel>我的 IP</MenuLabel>
        {others.map(agent => {
          const status = instanceStatus(agent);
          return (
            <MenuItem key={agent.id} href={destinations[agent.id] || `/agents/${encodeURIComponent(agent.id)}/#/today`}
              note={status.label === "已连接" ? undefined : status.label}>
              <span className="menu-agent"><AgentAvatar name={agent.name || ""} identity={agent.id} size="sm" /><span data-quoted="true">{agent.name || "未命名 Agent"}</span></span>
            </MenuItem>
          );
        })}
      </PopMenu>
    </div>
  );
}

export type StatusTone = "good" | "warning" | "critical" | "neutral";

/** Whether the Agent is there: 在线, 启动中 or 离线. */
export function StatusPill({ tone, label, title }: { tone: StatusTone; label: string; title?: string }) {
  return <span className="pa-badge agent-status" data-tone={tone} title={title}>
    {label}
  </span>;
}

export interface BarWorkspace { key: string; title: string; href: string }

/**
 * Whether the page around this frame has folded its navigation behind a button that floats over the frame's
 * top-left corner. The bar leaves that corner free; outside a frame the navigation shares the page and its own
 * stylesheet says so.
 */
export function useOuterNavButton(inFrame: boolean): boolean {
  const [folded, setFolded] = useState(false);
  useEffect(() => {
    if (!inFrame) return;
    let list: MediaQueryList;
    try { list = parent.matchMedia("(max-width: 1024px)"); } catch { return; }
    const sync = () => setFolded(list.matches);
    sync();
    list.addEventListener("change", sync);
    return () => list.removeEventListener("change", sync);
  }, [inFrame]);
  return folded;
}

/**
 * The one row above an Agent's work: whose it is, whether it is there, which work page is open, and the
 * few things that are not the work -- 待确认事项, 设置, and the rest under 更多. The page below it keeps no
 * heading of its own.
 */
export function AgentBar({
  agents,
  current,
  name,
  destinations,
  status,
  title,
  workspaces = [],
  activeWorkspace,
  navigationKey,
  visible = true,
  tools = {},
  pending = 0,
  onApprovals,
  onSettings,
  createHref,
  onOpenConversation,
  navButton = false,
  extra,
  meta,
}: {
  agents: HubAgent[] | null;
  current: string | null;
  /** This Agent's name, for when the directory has not been read. */
  name?: string;
  destinations?: Record<string, string>;
  status: { tone: StatusTone; label: string; title?: string } | null;
  /** Said when there is one work page open and nothing to choose between. */
  title?: string;
  workspaces?: BarWorkspace[];
  activeWorkspace?: string;
  /** An explicit route/return, independent of background page declarations. */
  navigationKey?: string | number;
  visible?: boolean;
  tools?: WorkTools;
  pending?: number;
  onApprovals: () => void;
  onSettings: () => void;
  /** The existing creation route, offered once in the interface-management group. */
  createHref?: string;
  /** The conversation is folded away: this brings it back. */
  onOpenConversation?: () => void;
  /** A button of the page around the frame floats over the bar's left corner: leave room for it. */
  navButton?: boolean;
  /** Controls that stand beside the three, e.g. the appearance menu of a page with no Hub around it. */
  extra?: ReactNode;
  /** The developer's own line. */
  meta?: ReactNode;
}) {
  const header = useRef<HTMLElement>(null);
  const [compact, setCompact] = useState(false);
  useLayoutEffect(() => {
    const bar = header.current;
    if (!bar) return;
    // The conversation and rail can leave a narrow work column on a wide screen.
    const changed = (width: number) => { if (width > 0) setCompact(width <= 680); };
    const measure = () => changed(bar.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(entries => {
        changed(entries[0]?.borderBoxSize?.[0]?.inlineSize ?? bar.getBoundingClientRect().width);
      });
      observer.observe(bar, { box: "border-box" });
      return () => observer.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  // The work's own pages are the row of tabs; with none, several work pages to choose between are; with one, its name.
  const taskPages = tools.pages?.items.length ? tools.pages : null;
  const tabs = taskPages ? { label: taskPages.label, items: taskPages.items.map(item => ({ key: item.href, title: item.label, href: item.href, current: item.active })) }
    : workspaces.length > 1 ? { label: "工作界面", items: workspaces.map(page => ({ key: page.key, title: page.title, href: page.href, current: page.key === activeWorkspace })) } : null;
  const hasWorkspaceSwitch = Boolean(taskPages && workspaces.length > 1);
  const hasMore = Boolean(hasWorkspaceSwitch || tools.reload || tools.links?.items.length || tools.change || createHref);
  const tabRow = useRef<HTMLElement>(null);
  const pendingReveal = useRef(true);
  useLayoutEffect(() => { pendingReveal.current = true; }, [navigationKey ?? activeWorkspace, visible, compact]);
  useLayoutEffect(() => {
    if (!visible || compact || !pendingReveal.current) return;
    const selected = tabRow.current?.querySelector<HTMLElement>("[aria-current=page]");
    if (!selected) return;
    selected.scrollIntoView({ behavior: "auto", block: "nearest", inline: "nearest" });
    pendingReveal.current = false;
  });
  return (
    <header ref={header} className={`agent-bar${navButton ? " has-nav-button" : ""}`} data-testid="agent-bar" data-compact={compact || undefined}>
      <AgentSwitch agents={agents ?? []} current={current} name={name} destinations={destinations} />
      {status ? <StatusPill {...status} /> : null}
      {compact && (tabs || tools.back) ? (
        <PopMenu label="工作界面与回程" className="agent-work-menu" trigger={<Icon name="content" size={18} />}>
          {tabs ? <MenuLabel>{tabs.label}</MenuLabel> : null}
          {tabs?.items.map(page => <MenuItem key={page.key} href={page.href} note={page.current ? "当前" : undefined}>{page.title}</MenuItem>)}
          {tools.back ? <MenuItem icon="back" href={tools.back.href}>{tools.back.label}</MenuItem> : null}
        </PopMenu>
      ) : tabs ? (
        <nav ref={tabRow} className="agent-tabs pa-tabs-list" aria-label={tabs.label}>
          {tabs.items.map(page => (
            <a key={page.key} href={page.href} aria-current={page.current ? "page" : undefined}>{page.title}</a>
          ))}
        </nav>
      ) : title ? <span className="agent-bar-title" data-quoted="true">{title}</span> : null}
      {!compact && tools.back ? <a className="btn pa-btn ghost agent-bar-back" href={tools.back.href}><Icon name="back" size={16} />{tools.back.label}</a> : null}
      <span className="agent-bar-gap" />
      {meta}
      {onOpenConversation ? (
        <button type="button" className="btn pa-btn ghost agent-bar-conversation" aria-label="打开对话" title="打开对话" aria-controls="owner-conversation" onClick={onOpenConversation}>
          <Icon name="chat" size={18} /><span className="agent-bar-conversation-label">对话</span>
        </button>
      ) : null}
      {extra}
      <button type="button" className="pa-icon-button agent-bar-bell" onClick={onApprovals}
        aria-label={`待确认事项${pending ? `，${pending} 条` : ""}`} title="待确认事项">
        <Icon name="waiting" size={18} />
        {pending ? <span className="pa-badge agent-bar-count num" aria-hidden="true">{pending}</span> : null}
      </button>
      <Tooltip content="设置"><IconButton name="settings" label="设置" onClick={onSettings} /></Tooltip>
      {hasMore ? <PopMenu label="更多" trigger={<Icon name="more" size={18} />}>
        {tools.reload ? (
          <MenuItem icon="refresh" disabled={tools.reload.busy || tools.reload.disabled}
            onSelect={() => { void tools.reload!.run(); }}>{tools.reload.busy ? <LightMotion state="working" compact /> : null}重新加载界面</MenuItem>
        ) : null}
        {hasWorkspaceSwitch ? <Dropdown.Group className="pa-open-group" aria-label="换一个界面">
          <MenuLabel>换一个界面</MenuLabel>
          {workspaces.map(page => <MenuItem key={page.key} href={page.href}>{page.title}</MenuItem>)}
        </Dropdown.Group> : null}
        {createHref ? <Dropdown.Group className="pa-open-group" aria-label="界面管理">
          <MenuLabel>界面管理</MenuLabel>
          <MenuItem icon="plus" href={createHref}>添加界面</MenuItem>
        </Dropdown.Group> : null}
        {tools.links?.items.length ? <Dropdown.Group className="pa-open-group" aria-label={tools.links.label}>
          <MenuLabel>{tools.links.label}</MenuLabel>
          {tools.links.items.map(link => <MenuItem key={link.href} href={link.href}>{link.label}</MenuItem>)}
        </Dropdown.Group> : null}
        {tools.change ? <Dropdown.Group className="pa-open-group" aria-label="最近一次调整">
          <MenuLabel>最近一次调整</MenuLabel>
          <p className="menu-note">{tools.change.agent} 按 <q data-quoted>{tools.change.task}</q> 调过</p>
          {tools.change.trace ? <MenuItem href={tools.change.trace}>查看执行过程</MenuItem> : null}
        </Dropdown.Group> : null}
      </PopMenu> : null}
    </header>
  );
}
